import LightningFS from "@isomorphic-git/lightning-fs";
import { logger } from "../../../../../core/logger.js";

export async function testFilesystemHealth({ logTags = "workspace|health|filesystem" } = {}) {
  try {
    if (!window.indexedDB) {
      logger.warn(
        { tags: logTags, color1: "yellow" },
        "Filesystem health check failed: IndexedDB not available"
      );
      console.warn(
        "[FILESYSTEM] Filesystem health check failed: IndexedDB not available"
      );
      return { healthy: false, error: "IndexedDB not available" };
    }

    const testFsName = `health-check-${Date.now()}`;
    const testFs = new LightningFS(testFsName);

    if (!testFs?.promises) {
      logger.warn(
        { tags: logTags, color1: "yellow" },
        "Filesystem health check failed: LightningFS promises not available"
      );
      console.warn(
        "[FILESYSTEM] Filesystem health check failed: LightningFS promises not available"
      );
      return { healthy: false, error: "LightningFS promises not available" };
    }

    const testPath = "/health-check-test.txt";
    const testContent = "health check test content";

    await testFs.promises.writeFile(testPath, testContent, "utf8");
    const readContent = await testFs.promises.readFile(testPath, "utf8");

    if (readContent !== testContent) {
      logger.warn(
        { tags: logTags, color1: "yellow" },
        "Filesystem health check failed: Read/write consistency failed"
      );
      console.warn(
        "[FILESYSTEM] Filesystem health check failed: Read/write consistency failed"
      );
      return { healthy: false, error: "Read/write consistency failed" };
    }

    try {
      await testFs.promises.unlink(testPath);
    } catch {
      // Ignore cleanup errors
    }

    logger.log({ tags: logTags, color1: "lime" }, "Filesystem health check passed");
    return { healthy: true };
  } catch (error) {
    logger.warn(
      { tags: logTags, color1: "yellow" },
      "Filesystem health check failed:",
      error.message || "Unknown filesystem error"
    );
    console.warn(
      "[FILESYSTEM] Filesystem health check failed:",
      error.message || "Unknown filesystem error"
    );
    return {
      healthy: false,
      error: error.message || "Unknown filesystem error",
    };
  }
}

export async function initializeBrowserFilesystem({
  appId,
  toolSystem,
  includeGit = true,
  logTags = "workspace|health|filesystem",
  fsPrefix = "revolt-app",
}) {
  if (!appId) {
    return { filesystem: null, git: null, fsName: null };
  }

  const healthCheck = await testFilesystemHealth({ logTags });
  if (!healthCheck.healthy) {
    throw new Error(`Filesystem health check failed: ${healthCheck.error}`);
  }

  const fsName = `${fsPrefix}-${appId}`;
  const filesystem = new LightningFS(fsName);

  let git = null;
  if (includeGit) {
    if (
      typeof window === "undefined" ||
      !window.git ||
      typeof window.git.init !== "function"
    ) {
      throw new Error(
        "isomorphic-git not available from CDN. Ensure script is loaded in index.html"
      );
    }
    git = window.git;
  }

  if (toolSystem) {
    toolSystem.appType = "browser";
    toolSystem.fs = filesystem;
    toolSystem.git = git;
  }

  return { filesystem, git, fsName };
}

/**
 * Initialize a dedicated chat-only LightningFS for an app.
 *
 * Chats (saved AI conversations) live in LightningFS for every app type — not just
 * browser apps — because they are independent of the app's own files (which for
 * `local` apps live on the native disk via IPC, and for `browser` apps live in
 * the main browser filesystem). This helper creates an isolated LightningFS
 * namespace for chats without touching `toolSystem` (so it does not override
 * `appType`/`fs`/`git` that the tool system uses for file operations).
 */
export async function initializeChatFilesystem({
  appId,
  logTags = "workspace|health|filesystem",
  fsPrefix = "revolt-chats",
}) {
  if (!appId) {
    return { filesystem: null, fsName: null };
  }

  const healthCheck = await testFilesystemHealth({ logTags });
  if (!healthCheck.healthy) {
    throw new Error(`Filesystem health check failed: ${healthCheck.error}`);
  }

  const fsName = `${fsPrefix}-${appId}`;
  const filesystem = new LightningFS(fsName);

  return { filesystem, fsName };
}
